import { OnDestroy, Injectable, Injector } from '@angular/core';

import { BehaviorSubject,  Subscription } from 'rxjs';

import { BlockModel } from '../../PageBuilder/models/block.model';
import { FullProfileAnimationModel } from '../../../Core/Dialogs/models/full-profile-animation.model';
import { FullProfileDialogAnimationsEnum } from '../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../Core/Dialogs/enums/overlay-animation.enum';
import { CoreDialogService } from '../../../Core/Dialogs/services/dialog.service';
import { MentionSearchCriteriaModel } from '../../../Monitor/Shared/models/mention-search-criteria.model';
import { MentionSearchService } from '../../../Monitor/Core/services/mention-search.service';
import { MonitorConfigService } from '../../MonitorShared/services/monitor-config.service';
import { MediaTypeEnum } from '@Common/Basic/enums/media-type.enum';
import { SearchStatesEnum } from '../../../Monitor/Shared/enums/search-states.enum';
import { DateQueryModel } from '../../Forms/models/date-query.model';
import { ChartModel } from '../../Charts/models/chart.model';
/**
 * Opened by name through BootstrapCoreDialogMap rather than imported: ChartDrillDownDialogComponent
 * extends ChartBlockComponent, which injects this service, so importing it here closes a cycle and
 * leaves whichever subclass of ChartBlockComponent loads first extending undefined.
 */
const chartDrillDownDialogKey: string = 'ChartDrillDownDialogComponent';
import { ChartGroupInputModel } from '@Common/Charts/models/chart-group-input.model';
import { CoverageInsightConfigModel } from '../models/coverage-insight-config.model';
import { ReportsDrillDownInterface } from '../interfaces/reports-drill-down.interface';
import { ChartDrillDownTabEnum } from '../enums/chart-drill-down-tab.enum';
import { DuplicatesStatusEnum } from '../../Filter/enums/duplicates-status.enum';
import { Am5Chart } from '@Common/Charts/models/am5-chart.model';
import { ReportSeriesModel } from '../models/report-series.model';


@Injectable()
export class ReportsChartDetailService implements OnDestroy, ReportsDrillDownInterface {
    overlayContainer: Element;
    chartDetailsDialogId: string;
    availableMediaTypes: MediaTypeEnum[];
    viewMentionsUrl: string;

    searchCriteria: MentionSearchCriteriaModel;
    cachedSearchQueryFilters: MentionSearchCriteriaModel;
    cachedDateQuery: DateQueryModel;
    dateQuery: DateQueryModel;
    showResetFilter$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    viewMentionsClicked$: BehaviorSubject<null> = new BehaviorSubject<null>(null);

    private subscriptions: Subscription[] = [];

    constructor(
        protected injector: Injector,
        public coreDialogService: CoreDialogService,
        private mentionSearchService: MentionSearchService,
        private monitorConfigService: MonitorConfigService,
    ) {
        this.subscriptions.push(
            this.monitorConfigService.getAvailableMediaTypes().subscribe(availableMediaTypes => {
                this.availableMediaTypes = availableMediaTypes;
            }),
            this.mentionSearchService.getSearchCriteria().subscribe(searchCriteria => {
                this.searchCriteria = searchCriteria;
            }),
            this.mentionSearchService.getDateQuery().subscribe(date => this.dateQuery = date),
            this.mentionSearchService.getSearchState().pipe()
                .subscribe(state => {
                    if (state.type === SearchStatesEnum.success && this.cachedSearchQueryFilters) {
                    // show reset filters info message if date range or filters are changed
                        this.showResetFilter$.next(
                            !this.cachedSearchQueryFilters.equals(this.mentionSearchService.generateFilterSearchCriteria())
                        || !(DateQueryModel.equals(this.cachedDateQuery, this.dateQuery))
                        );
                    }
                }),
        );
        this.overlayContainer = document.getElementById('main-content');
    }

    ngOnDestroy() {
        this.subscriptions.forEach(
            subscription => subscription.unsubscribe()
        );
        this.mentionSearchService.registerPagingValidator(null);
    }

    /**
     * open viewCoverageAnalysisDialog
     * inside viewCoverageAnalysisDialog is where he do all the handling of the mention searches based on chart segments
     */
    viewCoverageAnalysisDialog(
        url: string,
        block?: BlockModel,
        chart?: ChartModel | ChartGroupInputModel | Am5Chart,
        forceFilterQuery: boolean = false,
        selectedTabIndex?: ChartDrillDownTabEnum,
        coverageInsightConfig?: CoverageInsightConfigModel,
        reportSeries?: ReportSeriesModel[]
    ): void {
        this.viewMentionsClicked$.next(null);

        // HACK: for top terms, we have to re-opened it if clicked on
        if (this.chartDetailsDialogId) {
            this.coreDialogService.close(this.chartDetailsDialogId);
        }

        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const filterSearchCriteria: MentionSearchCriteriaModel = forceFilterQuery ? this.mentionSearchService.generateFilterSearchCriteria() : null;

        const {
            query: filterQuery = null,
            savedFilterIds = [],
            showSimilar = true,
        } = filterSearchCriteria ?? {};

        const ioModel =  {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer,
                block: block,
                chart: chart instanceof ChartModel ? chart : undefined,
                chartGroup: chart instanceof ChartGroupInputModel ? chart : undefined,
                am5Chart: chart instanceof Am5Chart ? chart : undefined,
                reportSeries: reportSeries,
                url: url,
                selectedTabIndex: selectedTabIndex ? selectedTabIndex : ChartDrillDownTabEnum.dashboard,
                coverageInsightConfig,
                forceAccountSearch: forceFilterQuery ? this.searchCriteria?.isAccountSearch : null,
                filterQuery,
                savedFilterIds,
                showSimilar,
            },
            outputs: {
                dialogClose: () => {
                    this.chartDetailsDialogId = null;
                }
            }
        };
        this.chartDetailsDialogId = this.coreDialogService.open(chartDrillDownDialogKey, ioModel, this.injector);
    }

    /**
     * reset the search to default chart segment
     */
    resetFilter(): void {
        this.mentionSearchService.clearFilters();
        this.mentionSearchService.clearSearch();
    }
}
